/**
 * Three-state auth gate for fire-and-forget telemetry.
 *
 * THE DEFECT THIS CLOSES. `logPerformanceMetric` and `logUsageEvent` are both
 * `withOrgAuth` callables, but the two things that call them fire before auth
 * exists: `reportWebVitals` runs at MODULE LOAD, and `trackPageView` runs on the
 * first route render. Every page load therefore produced 401s. Because delivery
 * is fire-and-forget, nobody saw them except the error channel, where they were
 * indistinguishable from a real outage.
 *
 * WHY THREE STATES AND NOT TWO. "Auth has not resolved yet" is a different fact
 * from "there is no signed-in user", and collapsing them loses real data:
 * LCP/FCP/TTFB all fire during precisely the unresolved window, so a two-state
 * gate would throw away most of the Web Vitals it was added to protect.
 *
 *   unknown   -> buffer (bounded), send nothing
 *   authed    -> flush the buffer once, then send live
 *   anonymous -> drop, and COUNT the drop
 *
 * 🔴 KEYED ON FIREBASE AUTH, NEVER ON A PROFILE FIELD. The only thing that
 * decides whether a `withOrgAuth` callable will accept a call is whether
 * Firebase holds an ID token. Keying this on an application profile field such
 * as `user.userId` would silently drop telemetry for every user whose profile
 * lacks that field while reporting itself as healthy. `authGateBinding.ts` is
 * the only place that reads Firebase; this module holds the policy.
 *
 * DROPS ARE COUNTED, NEVER SILENT — the same principle as `deliveryFailures.ts`:
 * a pipeline that cannot tell "delivered" from "dropped" has not measured
 * anything. Deliberately dependency-free (see `buildIdentity.ts` for why shared
 * telemetry code must not introduce bare specifiers).
 */

export type TelemetryAuthState = 'unknown' | 'authed' | 'anonymous';

/**
 * Cap on events held while auth is unresolved. Auth resolution is a
 * sub-second event, so this is generous; the cap exists so that a client that
 * never binds the gate leaks a bounded amount of memory and reports the
 * overflow rather than growing forever.
 */
export const TELEMETRY_BUFFER_LIMIT = 50;

export interface TelemetryGateStats {
  /** Current gate state. */
  readonly state: TelemetryAuthState;
  /** Events waiting for auth to resolve. */
  readonly buffered: number;
  /** Events released to the transport by a flush. */
  readonly flushed: number;
  /** Events discarded because auth resolved with no signed-in user. */
  readonly droppedAnonymous: number;
  /** Events discarded because the buffer was full before auth resolved. */
  readonly droppedOverflow: number;
}

/**
 * Telemetry is production-only: dev noise would swamp the Performance tab and
 * cost callable invocations for numbers nobody reads.
 *
 * WHY THIS IS A FUNCTION AND NOT AN INLINE `process.env.NODE_ENV` CHECK AT EACH
 * SENDER. react-scripts INLINES `process.env.NODE_ENV` at transform time, so
 * under the CI test harness the literal is baked to `'test'` and no amount of
 * assigning `process.env.NODE_ENV` at runtime changes it. With the check inlined
 * at each sender, every test of "the event WAS sent" silently asserted nothing —
 * the sender had already returned — while the sibling "was NOT sent" assertions
 * passed vacuously for the same reason. A named export can be mocked, so the
 * tests observe what they claim to observe.
 */
export function isTelemetryDeliveryEnabled(): boolean {
  return process.env.NODE_ENV === 'production';
}

type Send = () => void;

let state: TelemetryAuthState = 'unknown';
let buffer: Send[] = [];
let flushed = 0;
let droppedAnonymous = 0;
let droppedOverflow = 0;

/**
 * Run a sender without letting it escape. Telemetry must never be able to break
 * a page, and a throw mid-flush must not strand the events queued behind it.
 */
function runSafely(send: Send): void {
  try {
    send();
  } catch {
    /* a telemetry transport must not be able to break its caller */
  }
}

/**
 * Hand an event to the gate. `send` is invoked at most once: now if auth is
 * known-good, after the flush if auth has not resolved, never if there is no
 * user. Build the payload BEFORE calling this — the closure is what carries the
 * event's own timestamp and route through the buffering delay.
 */
export function sendWhenAuthed(send: Send): void {
  if (state === 'authed') {
    runSafely(send);
    return;
  }
  if (state === 'anonymous') {
    droppedAnonymous += 1;
    return;
  }
  if (buffer.length >= TELEMETRY_BUFFER_LIMIT) {
    // Drop the NEWEST, keep the earliest. Overflow only happens when auth never
    // resolved, and in that case the earliest events are the startup ones —
    // LCP/FCP/TTFB, the exact payload this buffer exists to protect. Evicting
    // the oldest would throw those away and keep whatever arrived last.
    droppedOverflow += 1;
    return;
  }
  buffer.push(send);
}

/**
 * Report what Firebase auth resolved to. Transitioning to `authed` releases
 * everything buffered, in emission order; transitioning to `anonymous` discards
 * it and counts the discard. Idempotent: re-reporting the same state does not
 * replay anything, because the buffer is emptied as it is consumed.
 */
export function setTelemetryAuthState(next: TelemetryAuthState): void {
  state = next;
  if (buffer.length === 0) return;

  const pending = buffer;
  buffer = [];

  if (next === 'authed') {
    flushed += pending.length;
    pending.forEach(runSafely);
  } else if (next === 'anonymous') {
    droppedAnonymous += pending.length;
  } else {
    // Back to unresolved: keep what we have rather than inventing a drop.
    buffer = pending;
  }
}

/** Current gate counters — the "did we measure it" side of the contract. */
export function getTelemetryGateStats(): TelemetryGateStats {
  return { state, buffered: buffer.length, flushed, droppedAnonymous, droppedOverflow };
}

/** Reset the gate. Tests only; a no-op in production builds. */
export function __resetTelemetryGateForTests(): void {
  if (process.env.NODE_ENV === 'production') return;
  state = 'unknown';
  buffer = [];
  flushed = 0;
  droppedAnonymous = 0;
  droppedOverflow = 0;
}
